import '@moreharvest/component-library/globals.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { ThemeProvider } from './context/ThemeProvider';
import { handleLocale, intlMessages } from './i18n';
import './index.css';

import { router } from './routes/router';
import { store } from './store';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 1,
        },
    },
});

// Expose queryClient for React Query DevTools Chrome extension
declare global {
    interface Window {
        __TANSTACK_QUERY_CLIENT__: typeof queryClient;
    }
}
window.__TANSTACK_QUERY_CLIENT__ = queryClient;

const body = document.body as HTMLElement;
body.className =
    'bg-base-background dark:bg-base-background-dark text-base-black dark:text-base-white';
const root = ReactDOM.createRoot(
    document.getElementById('home') as HTMLElement
);

const locale = handleLocale(navigator.language);

if (import.meta.env.VITE_MOCK_SERVER === 'true') {
    const { worker } = await import('./mocks/api/browser');
    await worker.start();
}

if (import.meta.env.PROD) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration(),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/yourserver\.io\/api/,
        ],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
}
root.render(
    <React.StrictMode>
        <ThemeProvider>
            <IntlProvider
                locale={locale}
                defaultLocale={'en'}
                messages={intlMessages[locale]}
            >
                <QueryClientProvider client={queryClient}>
                    <Provider store={store}>
                        <RouterProvider router={router} />
                    </Provider>
                    <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
            </IntlProvider>
        </ThemeProvider>
    </React.StrictMode>
);
